import React, { useState } from 'react';
import {
  TextField, Box, CircularProgress, Typography,
  Card, CardMedia, CardContent, CardActionArea,
  Chip, Button, ToggleButtonGroup, ToggleButton,
  Alert
} from '@mui/material';
import { Search, Store, ShoppingCart } from '@mui/icons-material';
import { searchProducts } from '../config/productProxy';

/**
 * ProductSearch — In-app product search using the proxy worker.
 * 
 * Props:
 *   onSelectProduct(product) — called when user taps a result
 *   compact (bool) — smaller card layout for sidebar use
 */
export default function ProductSearch({ onSelectProduct, compact = false }) {
  const [query, setQuery] = useState('');
  const [store, setStore] = useState(null); // null = both
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ amazon: [], walmart: [] });
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await searchProducts(query, store);
      if (store) {
        // Single-store search
        setResults({
          [store]: data.results || [],
          [store === 'amazon' ? 'walmart' : 'amazon']: []
        });
      } else {
        // Multi-store search
        setResults({
          amazon: data.amazon || [],
          walmart: data.walmart || []
        });
      }
      setSearched(true);
    } catch (err) {
      setError(err.message || 'Search failed. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const allResults = [...results.amazon, ...results.walmart];

  const handleSelect = (product) => {
    if (onSelectProduct) onSelectProduct(product);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box>
      {/* Search bar */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <TextField
          fullWidth
          size={compact ? 'small' : 'medium'}
          placeholder="Search for any product..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          InputProps={{
            startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          size={compact ? 'small' : 'medium'}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
        </Button>
      </Box>

      {/* Store filter */}
      <ToggleButtonGroup
        value={store}
        exclusive
        onChange={(_, val) => setStore(val)}
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value={null}>
          All Stores
        </ToggleButton>
        <ToggleButton value="amazon">
          <ShoppingCart sx={{ color: '#FF9900', mr: 0.5, fontSize: 18 }} />
          Amazon
        </ToggleButton>
        <ToggleButton value="walmart">
          <Store sx={{ color: '#0071CE', mr: 0.5, fontSize: 18 }} />
          Walmart
        </ToggleButton>
      </ToggleButtonGroup>

      {/* Error */}
      {error && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {searched && allResults.length === 0 && !loading && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No products found. Try different search terms.
        </Typography>
      )}

      {/* Results */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: compact ? 400 : 500, overflow: 'auto' }}>
        {allResults.map((product, i) => (
          <Card
            key={`${product.store}-${product.asin || product.itemId || i}`}
            variant="outlined"
            sx={{ flexShrink: 0 }}
          >
            <CardActionArea onClick={() => handleSelect(product)}>
              <Box sx={{ display: 'flex', p: compact ? 0.75 : 1 }}>
                {product.image && (
                  <CardMedia
                    component="img"
                    sx={{
                      width: compact ? 60 : 80,
                      height: compact ? 60 : 80,
                      objectFit: 'contain',
                      borderRadius: 1,
                      flexShrink: 0
                    }}
                    image={product.image}
                    alt={product.name}
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                )}
                <CardContent sx={{ flex: 1, py: 0, px: 1, '&:last-child': { pb: 0 } }}>
                  <Typography
                    variant={compact ? 'caption' : 'body2'}
                    fontWeight="medium"
                    sx={{ mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
                  >
                    {product.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {product.price != null && (
                      <Typography
                        variant={compact ? 'body2' : 'h6'}
                        color="primary"
                        fontWeight="bold"
                      >
                        ${product.price.toFixed(2)}
                      </Typography>
                    )}
                    <Chip
                      label={product.store === 'amazon' ? 'Amazon' : 'Walmart'}
                      size="small"
                      icon={product.store === 'amazon'
                        ? <ShoppingCart sx={{ fontSize: 14 }} />
                        : <Store sx={{ fontSize: 14 }} />
                      }
                      sx={{
                        bgcolor: product.store === 'amazon' ? '#FFF3E0' : '#E3F2FD',
                        color: product.store === 'amazon' ? '#E65100' : '#0D47A1',
                        fontWeight: 500
                      }}
                    />
                    {product.source === 'amazon-sponsored' && (
                      <Typography variant="caption" color="text.disabled">
                        Sponsored
                      </Typography>
                    )}
                  </Box>
                </CardContent>
              </Box>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
}
